import { CircleAlert, CircleCheckBig, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';

const TONES = {
  danger: { className: 'border-danger-muted bg-danger-subtle text-danger', Icon: CircleAlert },
  success: {
    className: 'border-success-muted bg-success-subtle text-success',
    Icon: CircleCheckBig,
  },
  warning: {
    className: 'border-warning-muted bg-warning-subtle text-warning',
    Icon: TriangleAlert,
  },
};

type NoticeProps = {
  tone: keyof typeof TONES;
  children: ReactNode;
  className?: string;
  /** Prende il focus appena compare: al posto di un modulo che sparisce dopo l'invio. */
  focusOnMount?: boolean;
  /** `false` per un avviso che fa parte del contenuto mostrato e non va annunciato. */
  live?: boolean;
};

/** Un avviso nel contenuto: l'errore si annuncia subito, il resto con calma. */
export default function Notice({
  tone,
  children,
  className,
  focusOnMount = false,
  live = true,
}: NoticeProps) {
  const { className: toneClass, Icon } = TONES[tone];
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);

  return (
    <div
      ref={ref}
      tabIndex={focusOnMount ? -1 : undefined}
      role={live ? (tone === 'danger' ? 'alert' : 'status') : undefined}
      className={cn(
        'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm focus-visible:focus-ring',
        toneClass,
        className
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}
